import { API, Auth, graphqlOperation } from 'aws-amplify'
import { isError } from 'lodash'

import * as mutations from './mutations'
import * as queries from './queries'

import { convertGraphqlError, notifyBugsnag } from '../helpers'
import {
  createEmptyResponseError,
  hasGraphqlData,
  registerEmptyResponse
} from '../helpers/graphql-response'
import { mailNotSentSpaceId } from '../helpers/invite-outcomes'
import { getNotification as getNotificationSingleton } from '../helpers/notification'
import { getUser as getUserSingleton } from '../helpers/user'

// AppSync antwortet mit 401, wenn der Access Token abgelaufen ist. Amplify
// refresht normalerweise selbst, nach Laptop-Standby gehen aber alle
// Refetches gleichzeitig mit dem alten Token raus. Dann einmal hart
// refreshen und den Request wiederholen. Parallele 401er teilen sich
// denselben Refresh.
const isUnauthorized = (err) =>
  err?.errors?.[0]?.originalError?.response?.status === 401

// AppSync-Auth hat den Zugriff abgelehnt, auf die Operation oder auf ein Feld
// mit eigener @auth-Regel. Typischer Fall ist ein Link auf eine Stelle eines
// anderen Partners, das Token traegt nur die Gruppen des aktiven Space.
const isNotAuthorizedToAccess = (err) =>
  /^Not Authorized to access \S+ on type \S+$/.test(
    err?.errors?.[0]?.message ?? ''
  )

// Antwort ohne data und ohne errors (Proxy, Captive Portal, Extension).
// Queries werden einmal wiederholt, Mutations nicht, weil der Request AppSync
// erreicht haben kann und ein Retry doppelt anlegen wuerde.
const EMPTY_RESPONSE_RETRY_DELAY_MS = 1000

let refreshPromise = null

const forceRefreshSession = async () => {
  const user = await Auth.currentAuthenticatedUser()
  const refreshToken = user.getSignInUserSession().getRefreshToken()

  await new Promise((resolve, reject) => {
    user.refreshSession(refreshToken, (err, session) =>
      err ? reject(err) : resolve(session)
    )
  })
}

const refreshSessionOnce = () => {
  if (!refreshPromise) {
    refreshPromise = forceRefreshSession().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

export function query({ query, variables = {}, authMode, options = {} }) {
  if (!query) throw new Error('Missing parameter')

  const { error, not_authorized_error_message } = getNotificationSingleton()

  const promise = new Promise((resolve, reject) => {
    const possibleItems = []
    let emptyAttempts = []

    // MAIL_NOT_SENT meldet inviteTalent selbst an Bugsnag, das Dashboard
    // schickt die Mail nach. Sonst gäbe eine gedrosselte Mehrfach-Einladung
    // ein Event je Adresse.
    const rejectPromise = (err) => {
      if (!mailNotSentSpaceId(err)) notifyBugsnag(err)
      reject(err)
    }

    const request = (nextToken, isRetry = false) => {
      const startedAt = Date.now()

      API.graphql({
        ...graphqlOperation(queries[query], {
          limit: 200, // can be overwritten by variables
          ...variables,
          nextToken
        }),
        authMode
      })
        .then((response) => {
          const { data, errors } = response ?? {}
          const origin = options.origin || query

          if (errors) {
            rejectPromise(convertGraphqlError({ errors }, { query, variables }))
            return
          }

          const reportEmptyResponse = (outcome) =>
            createEmptyResponseError({
              kind: 'query',
              operation: query,
              variables,
              authMode,
              hasNextToken: Boolean(nextToken),
              outcome,
              attempts: emptyAttempts
            })

          if (!hasGraphqlData(response)) {
            registerEmptyResponse()
            emptyAttempts.push({
              response,
              duration_ms: Date.now() - startedAt
            })

            if (isRetry) {
              rejectPromise(reportEmptyResponse('retry_failed'))
              return
            }

            setTimeout(
              () => request(nextToken, true),
              EMPTY_RESPONSE_RETRY_DELAY_MS
            )
            return
          }

          if (emptyAttempts.length) {
            // Retry hat geholfen. Der Aussetzer bleibt als Info in Bugsnag.
            notifyBugsnag(reportEmptyResponse('recovered'))
            emptyAttempts = []
          }

          if (!data[origin]) {
            resolve(null)
            return
          }

          if (data[origin].items) {
            const items = data[origin].items
            const nextToken = data[origin].nextToken

            possibleItems.push(...items)

            if (nextToken) request(nextToken)
            else resolve(possibleItems)
          } else {
            resolve(data[origin])
          }
        })
        .catch((err) => {
          if (isError(err)) {
            rejectPromise(err)
            return
          }

          if (!isRetry && isUnauthorized(err)) {
            refreshSessionOnce()
              .then(() => request(nextToken, true))
              .catch(() =>
                rejectPromise(convertGraphqlError(err, { query, variables }))
              )
            return
          }

          const graphql_error = convertGraphqlError(err, { query, variables })

          if (
            graphql_error.message.includes(`${query} / NoPermissionsError`) ||
            isNotAuthorizedToAccess(err)
          ) {
            // reject the error and show error notification
            reject(new Error('NOT_AUTHORIZED'))
            error(not_authorized_error_message)
            return
          }

          rejectPromise(graphql_error)
        })
    }

    request()
  })

  return promise
}

export function mutation({ mutation, input, authMode, options = {} }) {
  const { username } = getUserSingleton()
  const { error, not_authorized_error_message } = getNotificationSingleton()

  if (!username && !input.lastUpdatedBy) {
    throw new Error('graphql mutation error: username not found')
  }

  if (
    !options.disableLastUpdatedBy &&
    !mutation.includes('delete') &&
    !input.lastUpdatedBy
  ) {
    input.lastUpdatedBy = username
  }

  const promise = new Promise((resolve, reject) => {
    const rejectPromise = (err) => {
      notifyBugsnag(err)
      reject(err)
    }

    const request = (isRetry = false) => {
      const startedAt = Date.now()

      API.graphql({
        ...graphqlOperation(mutations[mutation], { input }),
        authMode
      })
        .then((response) => {
          const { data, errors } = response ?? {}

          if (errors) {
            rejectPromise(convertGraphqlError({ errors }, { mutation, input }))
            return
          }

          if (!hasGraphqlData(response)) {
            registerEmptyResponse()
            rejectPromise(
              createEmptyResponseError({
                kind: 'mutation',
                operation: mutation,
                input,
                authMode,
                outcome: 'not_retried',
                attempts: [{ response, duration_ms: Date.now() - startedAt }]
              })
            )
            return
          }

          resolve(data[mutation])
        })
        .catch((err) => handleError(err, isRetry))
    }

    const handleError = (err, isRetry) => {
      if (isError(err)) {
        rejectPromise(err)
        return
      }

      if (!isRetry && isUnauthorized(err)) {
        refreshSessionOnce()
          .then(() => request(true))
          .catch(() =>
            rejectPromise(convertGraphqlError(err, { mutation, input }))
          )
        return
      }

      const graphql_error = convertGraphqlError(err, { mutation, input })

      if (
        graphql_error.message.includes(
          `Not Authorized to access ${mutation}`
        ) ||
        graphql_error.message.includes(`${mutation} / Unauthorized on`)
      ) {
        // reject the error and show error notification
        reject(new Error('NOT_AUTHORIZED'))
        error(not_authorized_error_message)
        return
      }

      rejectPromise(graphql_error)
    }

    request()
  })

  return promise
}

export function currentAuthenticatedUser() {
  return Auth.currentAuthenticatedUser({ bypassCache: true })
}
